import { wsClient } from './websocket';
import { shouldCancelOutgoingCallSession } from '../call/callSessionEndGuards';
import { configAPI } from '../api/client';
import { extractDTLSFingerprint, signDTLSFingerprintWithKey, verifyDTLSSignature } from '../crypto/dtls';
import type { CallSessionId } from '@shared/types';
import type { SigningKey } from '../crypto/ed25519Provider';
import { signRequestWithKey } from '../crypto/signing';

const isWebrtcDebugEnabled = (): boolean => {
  try {
    return import.meta.env.DEV || localStorage.getItem('webrtcDebug') === '1';
  } catch {
    return import.meta.env.DEV;
  }
};

const webrtcDebugLog = (...args: unknown[]) => {
  if (!isWebrtcDebugEnabled()) return;
  // Keep logs easily searchable.
  console.log(...args);
};

const summarizeVideoSdp = (tag: string, sdp?: string | null) => {
  if (!sdp) {
    webrtcDebugLog(`[WebRTC][sdp] ${tag}`, { hasSdp: false });
    return;
  }
  const lines = sdp.split(/\r?\n/);
  const media: Array<{ mid?: string; direction?: string; mline?: string }> = [];
  let inVideo = false;
  let current: { mid?: string; direction?: string; mline?: string } | null = null;
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('m=')) {
      if (current) media.push(current);
      inVideo = line.startsWith('m=video ');
      current = inVideo ? { mline: line } : null;
      continue;
    }
    if (!inVideo || !current) continue;
    if (line.startsWith('a=mid:')) current.mid = line.slice('a=mid:'.length);
    if (line === 'a=sendrecv' || line === 'a=sendonly' || line === 'a=recvonly' || line === 'a=inactive') {
      current.direction = line.slice(2);
    }
  }
  if (current) media.push(current);
  webrtcDebugLog(`[WebRTC][sdp] ${tag}`, {
    videoSections: media.length,
    media
  });
};

const getVideoSdpSections = (sdp?: string | null): Array<{ mid?: string; direction?: string; mline?: string }> => {
  if (!sdp) return [];
  const lines = sdp.split(/\r?\n/);
  const media: Array<{ mid?: string; direction?: string; mline?: string }> = [];
  let inVideo = false;
  let current: { mid?: string; direction?: string; mline?: string } | null = null;
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (line.startsWith('m=')) {
      if (current) media.push(current);
      inVideo = line.startsWith('m=video ');
      current = inVideo ? { mline: line } : null;
      continue;
    }
    if (!inVideo || !current) continue;
    if (line.startsWith('a=mid:')) current.mid = line.slice('a=mid:'.length);
    if (line === 'a=sendrecv' || line === 'a=sendonly' || line === 'a=recvonly' || line === 'a=inactive') {
      current.direction = line.slice(2);
    }
  }
  if (current) media.push(current);
  return media;
};

const remoteSdpWillSendVideo = (sdp?: string | null): boolean => {
  return getVideoSdpSections(sdp).some((section) =>
    section.direction === 'sendrecv' || section.direction === 'sendonly'
  );
};

interface WebRTCCallbacks {
  onRemoteStream?: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onIceConnectionStateChange?: (state: RTCIceConnectionState) => void;
  onCallEnded?: () => void;
  onSecurityVerified?: (verified: boolean) => void;
}

export interface IceCandidateDebugInfo {
  candidateType?: string;
  protocol?: string;
  address?: string;
  ip?: string;
  port?: number;
  url?: string;
  relayProtocol?: string;
  networkType?: string;
}

export interface IceCandidatePairDebugInfo {
  state?: string;
  nominated?: boolean;
  currentRoundTripTime?: number;
  availableOutgoingBitrate?: number;
  bytesSent?: number;
  bytesReceived?: number;
}

export interface IceDebugInfo {
  connectionState: RTCPeerConnectionState;
  iceConnectionState: RTCIceConnectionState;
  iceGatheringState: RTCIceGatheringState;
  local?: IceCandidateDebugInfo;
  remote?: IceCandidateDebugInfo;
  pair?: IceCandidatePairDebugInfo;
}

type CallAuthContext = {
  deviceId?: string;
  identityId?: string;
  identitySigningKey?: SigningKey;
  deviceSigningKey?: SigningKey;
  serverUrl?: string;
};

type RemoteReceiverEntry = {
  receiver: RTCRtpReceiver;
  track: MediaStreamTrack;
  score: number;
  statsSummary: { bytesReceived?: number; packetsReceived?: number; framesDecoded?: number } | null;
  appearsActiveNow: boolean;
};

class WebRTCManager {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private videoSender: RTCRtpSender | null = null;
  private preferredVideoFacingMode: 'user' | 'environment' = 'user';
  private selectedAudioInputDeviceId: string | null = null;
  private selectedVideoInputDeviceId: string | null = null;
  private screenShareStream: MediaStream | null = null;
  private previousCameraTrack: MediaStreamTrack | null = null;
  private previousAudioTrack: MediaStreamTrack | null = null;
  private screenShareAudioContext: AudioContext | null = null;
  private screenShareMixedAudioTrack: MediaStreamTrack | null = null;
  private observedRemoteTrackIds: Set<string> = new Set();
  private pendingRenegotiationReason: string | null = null;
  private renegotiationInFlight = false;
  private iceServers: RTCIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ];

  private callbacks: WebRTCCallbacks = {};
  private currentCallSessionId: CallSessionId | null = null;
  private currentTargetIdentityId: string | null = null;
  private currentCallAuthContext: CallAuthContext | null = null;
  private pendingIceCandidates: Map<CallSessionId, RTCIceCandidateInit[]> = new Map();
  private pendingOutgoingIceCandidates: Map<CallSessionId, RTCIceCandidateInit[]> = new Map();
  private signalingReadyForIce: Set<CallSessionId> = new Set();
  private observedLocalTrackIds: Set<string> = new Set();
  private pendingRemoteReceiverResyncTimer: number | null = null;
  private pendingRemoteReceiverResyncAttempts = 0;
  private remoteVideoTrackMuteTimers: Map<string, number> = new Map();
  private remoteVideoReceiverSnapshots: Map<string, {
    bytesReceived: number;
    packetsReceived: number;
    framesDecoded: number;
    stagnantSamples: number;
  }> = new Map();
  // Set to true in answerCall when video is enabled; cleared after the first 'connected' event
  // triggers a renegotiation to work around Chrome not starting video RTP after an initial answer.
  private pendingPostConnectVideoRenegotiation = false;

  private releaseLocalMediaTracks(): void {
    if (!this.localStream) return;
    try {
      this.localStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
    } finally {
      this.localStream = null;
    }
  }

  private enqueueOutgoingIceCandidate(callSessionId: CallSessionId, candidate: RTCIceCandidateInit): void {
    const list = this.pendingOutgoingIceCandidates.get(callSessionId) || [];
    list.push(candidate);
    this.pendingOutgoingIceCandidates.set(callSessionId, list);
  }

  private flushOutgoingIceCandidates(callSessionId: CallSessionId): void {
    const list = this.pendingOutgoingIceCandidates.get(callSessionId);
    if (!list || list.length === 0) return;

    this.pendingOutgoingIceCandidates.delete(callSessionId);
    for (const candidate of list) {
      wsClient.sendIceCandidate(callSessionId, candidate);
    }
  }

  private markSignalingReadyForIce(callSessionId: CallSessionId): void {
    this.signalingReadyForIce.add(callSessionId);
    this.flushOutgoingIceCandidates(callSessionId);
  }

  private requestRenegotiation(reason: string): void {
    this.pendingRenegotiationReason = reason;
    void this.flushPendingRenegotiation('request');
  }

  private async buildRemoteReceiverEntries(): Promise<RemoteReceiverEntry[]> {
    const receivers = this.peerConnection?.getReceivers() || [];
    const receiverEntries = await Promise.all(receivers.map(async (receiver) => {
      const track = receiver.track;
      if (!track || track.readyState === 'ended') return null;

      let score = 0;
      let statsSummary: { bytesReceived?: number; packetsReceived?: number; framesDecoded?: number } | null = null;
      let appearsActiveNow = track.kind !== 'video';
      if (track.kind === 'video') {
        try {
          const report = await receiver.getStats();
          for (const stat of report.values()) {
            if (stat.type !== 'inbound-rtp') continue;
            const kind = (stat as RTCInboundRtpStreamStats).kind
              || ((stat as any).mediaType as string | undefined);
            if (kind !== 'video') continue;
            const framesDecoded = Number((stat as RTCInboundRtpStreamStats).framesDecoded || 0);
            const packetsReceived = Number((stat as RTCInboundRtpStreamStats).packetsReceived || 0);
            const bytesReceived = Number((stat as RTCInboundRtpStreamStats).bytesReceived || 0);
            score = (framesDecoded * 1_000_000) + (packetsReceived * 1_000) + bytesReceived;
            statsSummary = { framesDecoded, packetsReceived, bytesReceived };
            appearsActiveNow = this.updateRemoteVideoReceiverActivity(track.id, {
              framesDecoded,
              packetsReceived,
              bytesReceived
            }, track.muted);
            break;
          }
        } catch {
          // ignore stats failure
        }
        if (!statsSummary) {
          appearsActiveNow = !track.muted;
        }
        if (!track.muted) score += 10;
        if (track.enabled) score += 1;
      }

      return { receiver, track, score, statsSummary, appearsActiveNow };
    }));

    return receiverEntries.filter((entry): entry is RemoteReceiverEntry => !!entry);
  }

  private updateRemoteVideoReceiverActivity(
    trackId: string,
    stats: { bytesReceived: number; packetsReceived: number; framesDecoded: number },
    muted: boolean
  ): boolean {
    const previous = this.remoteVideoReceiverSnapshots.get(trackId);
    const grew = !!previous && (
      stats.bytesReceived > previous.bytesReceived
      || stats.packetsReceived > previous.packetsReceived
      || stats.framesDecoded > previous.framesDecoded
    );
    const stagnantSamples = grew
      ? 0
      : ((previous?.stagnantSamples || 0) + 1);

    this.remoteVideoReceiverSnapshots.set(trackId, {
      ...stats,
      stagnantSamples
    });

    if (grew) return true;
    if (!previous) return !muted;
    if (muted) return false;
    return stagnantSamples < 2;
  }

  private pickPreferredRemoteVideoEntry(entries: RemoteReceiverEntry[]): RemoteReceiverEntry | null {
    return entries
      .filter((entry) => entry.track.kind === 'video')
      .filter((entry) => entry.appearsActiveNow)
      .sort((a, b) => a.score - b.score)
      .pop() || null;
  }

  private syncRemoteStreamFromReceivers(tag: string): void {
    if (!this.peerConnection) return;

    const run = async () => {
      const activeEntries = await this.buildRemoteReceiverEntries();
      const audioTracks = activeEntries
        .map((entry) => entry.track)
        .filter((track) => track.kind === 'audio');
      const preferredVideoEntry = this.pickPreferredRemoteVideoEntry(activeEntries);
      const preferredVideoTrack = preferredVideoEntry?.track || null;
      const nextTracks = preferredVideoTrack ? [...audioTracks, preferredVideoTrack] : audioTracks;

      webrtcDebugLog('[RemoteVideo][sync-from-receivers]', JSON.stringify({
        tag,
        receiverTracks: activeEntries.map((entry) => ({
          kind: entry.track.kind,
          id: entry.track.id,
          enabled: entry.track.enabled,
          muted: entry.track.muted,
          readyState: entry.track.readyState,
          score: entry.score,
          stats: entry.statsSummary,
          appearsActiveNow: entry.appearsActiveNow
        })),
        chosenVideoTrackId: preferredVideoTrack?.id || null
      }));

      if (nextTracks.length === 0) {
        this.remoteStream = null;
        if (this.callbacks.onRemoteStream) {
          this.callbacks.onRemoteStream(new MediaStream());
        }
        return;
      }

      const nextStream = new MediaStream();
      nextTracks.forEach((track) => nextStream.addTrack(track));
      this.remoteStream = nextStream;

      if (this.callbacks.onRemoteStream) {
        this.callbacks.onRemoteStream(this.remoteStream);
      }
    };

    void run();
  }

  private scheduleRemoteStreamReceiverResync(tag: string): void {
    if (this.pendingRemoteReceiverResyncTimer !== null) {
      window.clearTimeout(this.pendingRemoteReceiverResyncTimer);
      this.pendingRemoteReceiverResyncTimer = null;
    }

    this.pendingRemoteReceiverResyncAttempts = 3;
    const tick = () => {
      this.syncRemoteStreamFromReceivers(`${tag}:retry${4 - this.pendingRemoteReceiverResyncAttempts}`);
      this.pendingRemoteReceiverResyncAttempts -= 1;
      if (this.pendingRemoteReceiverResyncAttempts <= 0) {
        this.pendingRemoteReceiverResyncTimer = null;
        return;
      }
      this.pendingRemoteReceiverResyncTimer = window.setTimeout(tick, 500);
    };

    this.pendingRemoteReceiverResyncTimer = window.setTimeout(tick, 350);
  }

  private clearRemoteVideoTracks(reason: string): void {
    for (const timer of this.remoteVideoTrackMuteTimers.values()) {
      window.clearTimeout(timer);
    }
    this.remoteVideoTrackMuteTimers.clear();
    if (this.remoteStream) {
      for (const track of this.remoteStream.getVideoTracks()) {
        try {
          this.remoteStream.removeTrack(track);
        } catch {
          // ignore
        }
      }
      if (this.remoteStream.getTracks().length === 0) {
        this.remoteStream = null;
      }
    }
    webrtcDebugLog('[WebRTC][remoteVideo] cleared remote video tracks', { reason });
    if (this.callbacks.onRemoteStream) {
      this.callbacks.onRemoteStream(this.remoteStream ?? new MediaStream());
    }
  }

  private removeRemoteVideoTrack(track: MediaStreamTrack, reason: string): void {
    const pendingTimer = this.remoteVideoTrackMuteTimers.get(track.id);
    if (pendingTimer !== undefined) {
      window.clearTimeout(pendingTimer);
      this.remoteVideoTrackMuteTimers.delete(track.id);
    }

    if (!this.remoteStream) return;

    let removed = false;
    for (const existingTrack of this.remoteStream.getVideoTracks()) {
      if (existingTrack.id !== track.id) continue;
      try {
        this.remoteStream.removeTrack(existingTrack);
        removed = true;
      } catch {
        // ignore
      }
    }

    if (!removed) return;

    if (this.remoteStream.getTracks().length === 0) {
      this.remoteStream = null;
    }

    webrtcDebugLog('[WebRTC][remoteVideo] removed remote video track', {
      reason,
      trackId: track.id,
      remainingTracks: this.remoteStream?.getTracks().map((t) => ({
        kind: t.kind,
        id: t.id,
        muted: t.muted,
        readyState: t.readyState
      })) ?? []
    });

    if (this.callbacks.onRemoteStream) {
      this.callbacks.onRemoteStream(this.remoteStream ?? new MediaStream());
    }
  }

  private applyRemoteVideoStateFromSdp(sdp: string | null | undefined, tag: string): void {
    if (!remoteSdpWillSendVideo(sdp)) {
      this.clearRemoteVideoTracks(`${tag}:no-remote-video-send`);
      return;
    }

    this.syncRemoteStreamFromReceivers(tag);
    this.scheduleRemoteStreamReceiverResync(tag);
  }

  private async applyRemoteDescriptionAndVideoState(
    description: RTCSessionDescriptionInit,
    tag: string
  ): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not ready');
    }

    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(description));
    this.applyRemoteVideoStateFromSdp(description.sdp, tag);
    this.pickNegotiatedVideoSender(`${tag}:after setRemoteDescription`);
    await this.ensureLocalVideoTrackAttached(`${tag}:after setRemoteDescription`);

    if (this.currentCallSessionId) {
      await this.flushIceCandidates(this.currentCallSessionId);
    }
  }

  private async flushPendingRenegotiation(source: string): Promise<void> {
    if (!this.peerConnection || !this.currentCallSessionId) return;
    if (this.renegotiationInFlight) return;

    const pc = this.peerConnection;
    if (pc.signalingState !== 'stable') {
      webrtcDebugLog('[WebRTC][renegotiate] queued (signalingState not stable)', {
        source,
        pendingReason: this.pendingRenegotiationReason,
        signalingState: pc.signalingState
      });
      return;
    }

    const reason = this.pendingRenegotiationReason;
    if (!reason) return;

    this.pendingRenegotiationReason = null;
    this.renegotiationInFlight = true;

    try {
      webrtcDebugLog('[WebRTC][renegotiate] creating offer', { reason, source });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // E2EE: sign DTLS fingerprint for renegotiation as well.
      let dtlsSignature: string | undefined;
      const identityPrivateKey = this.getCurrentIdentitySigningKey();
      if (identityPrivateKey && offer.sdp) {
        const fingerprint = extractDTLSFingerprint(offer.sdp);
        if (fingerprint) {
          dtlsSignature = await signDTLSFingerprintWithKey(fingerprint, identityPrivateKey);
        }
      }

      wsClient.renegotiateOffer(this.currentCallSessionId, offer, dtlsSignature);
      this.dumpPeerConnectionState(`renegotiateSdp:${reason}:after setLocalDescription`);
    } catch (e) {
      webrtcDebugLog('[WebRTC][renegotiate] failed', { reason, source, error: e });
      // Best-effort: re-queue so it can be retried when stable.
      this.pendingRenegotiationReason = reason;
    } finally {
      this.renegotiationInFlight = false;
    }
  }

  private dumpPeerConnectionState(tag: string) {
    if (!isWebrtcDebugEnabled()) return;
    if (!this.peerConnection) return;

    try {
      const pc = this.peerConnection;
      webrtcDebugLog(`[WebRTC][pc] ${tag}`, {
        signalingState: pc.signalingState,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
        transceivers: pc.getTransceivers().map((t) => ({
          mid: t.mid,
          direction: t.direction,
          currentDirection: t.currentDirection,
          senderTrack: t.sender?.track
            ? { kind: t.sender.track.kind, id: t.sender.track.id, readyState: t.sender.track.readyState }
            : null,
          receiverTrack: t.receiver?.track
            ? { kind: t.receiver.track.kind, id: t.receiver.track.id, readyState: t.receiver.track.readyState, muted: t.receiver.track.muted }
            : null
        }))
      });
    } catch (e) {
      webrtcDebugLog('[WebRTC][pc] dump failed', e);
    }
  }

  private pickNegotiatedVideoSender(tag: string) {
    if (!this.peerConnection) return;
    try {
      const pc = this.peerConnection;
      const videoTs = pc
        .getTransceivers()
        .filter((t) => t.receiver?.track?.kind === 'video' || t.sender?.track?.kind === 'video');

      // Prefer a negotiated (mid assigned) video transceiver if present.
      const chosen = videoTs.find((t) => t.mid !== null) || videoTs[0];

      if (!chosen?.sender) return;

      const prevSender = this.videoSender;
      this.videoSender = chosen.sender;

      webrtcDebugLog(`[WebRTC][videoSender] rebound (${tag})`, {
        mid: chosen.mid,
        direction: chosen.direction,
        currentDirection: chosen.currentDirection,
        prevTrackId: prevSender?.track?.id || null,
        newTrackId: this.videoSender.track?.id || null
      });
    } catch (e) {
      webrtcDebugLog('[WebRTC][videoSender] rebound failed', e);
    }
  }

  private trySetVideoDirection(direction: RTCRtpTransceiverDirection, tag: string) {
    if (!this.peerConnection) return;
    try {
      const videoT = this.peerConnection
        .getTransceivers()
        .find((t) => t.receiver?.track?.kind === 'video' || t.sender?.track?.kind === 'video');
      if (!videoT) return;
      if (videoT.direction === direction) return;
      videoT.direction = direction;
      webrtcDebugLog('[WebRTC][videoTransceiver] set direction', {
        tag,
        mid: videoT.mid,
        direction: videoT.direction,
        currentDirection: videoT.currentDirection
      });
    } catch (e) {
      webrtcDebugLog('[WebRTC][videoTransceiver] failed to set direction', { tag, direction, error: e });
    }
  }

  private async ensureLocalVideoTrackAttached(tag: string): Promise<void> {
    if (!this.peerConnection || !this.localStream) return;
    if (!this.videoSender) return;

    const localVideoTrack = this.localStream.getVideoTracks()[0];
    if (!localVideoTrack) return;

    const senderTrackId = this.videoSender.track?.id || null;
    if (senderTrackId === localVideoTrack.id) return;

    try {
      this.trySetVideoDirection('sendrecv', `ensureLocalVideoTrackAttached:${tag}`);
      await this.videoSender.replaceTrack(localVideoTrack);
      webrtcDebugLog('[WebRTC][videoSender] ensured local video track attached', {
        tag,
        localTrackId: localVideoTrack.id,
        prevSenderTrackId: senderTrackId
      });
    } catch (e) {
      webrtcDebugLog('[WebRTC][videoSender] failed to ensure local video track attached', { tag, error: e });
    }
  }

  private getAlwaysTurnEnabled(): boolean {
    try {
      return localStorage.getItem('webrtcAlwaysTurn') === '1';
    } catch {
      return false;
    }
  }

  private isTurnServer(server: RTCIceServer): boolean {
    const serverUrls = Array.isArray(server.urls) ? server.urls : [server.urls];
    return serverUrls.some((url) => typeof url === 'string' && (url.startsWith('turn:') || url.startsWith('turns:')));
  }

  private getIceServersForCall(): RTCIceServer[] {
    const alwaysTurn = this.getAlwaysTurnEnabled();
    if (!alwaysTurn) return this.iceServers;
    return this.iceServers.filter((server) => this.isTurnServer(server));
  }

  private isRelayCandidate(candidate: RTCIceCandidateInit): boolean {
    return typeof candidate.candidate === 'string' && candidate.candidate.includes(' typ relay ');
  }

  private parseCandidateForLog(candidate: RTCIceCandidateInit): {
    type: string;
    protocol: string;
    address: string;
    port: string;
  } {
    const raw = typeof candidate?.candidate === 'string' ? candidate.candidate : '';
    const type = (raw.match(/ typ ([a-z0-9]+)/i)?.[1] || 'unknown').toLowerCase();
    const protocol = (raw.match(/^candidate:[^\s]+\s+\d+\s+(udp|tcp)\s+/i)?.[1] || 'unknown').toLowerCase();
    const addrMatch = raw.match(/^candidate:[^\s]+\s+\d+\s+(?:udp|tcp)\s+\d+\s+([^\s]+)\s+(\d+)/i);
    return {
      type,
      protocol,
      address: addrMatch?.[1] || '-',
      port: addrMatch?.[2] || '-'
    };
  }

  private getCurrentCallAuthContext(): CallAuthContext | null {
    const current = this.currentCallAuthContext;
    if (current?.deviceId || current?.identityId || current?.identitySigningKey || current?.deviceSigningKey || current?.serverUrl) {
      return current;
    }
    return null;
  }

  private setCurrentCallAuthContext(context?: CallAuthContext | null): void {
    const deviceId = (context?.deviceId || '').toString().trim();
    const identityId = (context?.identityId || '').toString().trim();
    const serverUrl = (context?.serverUrl || '').toString().trim();
    const identitySigningKey = context?.identitySigningKey || undefined;
    const deviceSigningKey = context?.deviceSigningKey || undefined;

    if (!deviceId && !identityId && !serverUrl && !identitySigningKey && !deviceSigningKey) {
      this.currentCallAuthContext = null;
      return;
    }

    this.currentCallAuthContext = {
      ...(deviceId ? { deviceId } : {}),
      ...(identityId ? { identityId } : {}),
      ...(serverUrl ? { serverUrl } : {}),
      ...(identitySigningKey ? { identitySigningKey } : {}),
      ...(deviceSigningKey ? { deviceSigningKey } : {})
    };
  }

  private getCurrentIdentitySigningKey(): SigningKey | null {
    return this.getCurrentCallAuthContext()?.identitySigningKey || null;
  }

  private getAudioSender(): RTCRtpSender | null {
    if (!this.peerConnection) return null;
    return this.peerConnection.getSenders().find((sender) => sender.track?.kind === 'audio') || null;
  }

  private async getVideoInputDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === 'videoinput');
    } catch {
      return [];
    }
  }

  private detectFacingModeHint(value?: string | null): 'user' | 'environment' | null {
    const normalized = (value || '').toLowerCase();
    if (!normalized) return null;
    if (/(back|rear|environment|world|traseira)/.test(normalized)) return 'environment';
    if (/(front|user|face|selfie|facetime)/.test(normalized)) return 'user';
    return null;
  }

  private inferFacingMode(device?: Pick<MediaDeviceInfo, 'label'> | null, fallback?: string | null): 'user' | 'environment' {
    return this.detectFacingModeHint(`${device?.label || ''} ${fallback || ''}`) || 'user';
  }

  private detectTrackFacingMode(
    track?: MediaStreamTrack | null,
    device?: Pick<MediaDeviceInfo, 'label'> | null
  ): 'user' | 'environment' | null {
    if (!track) {
      return this.detectFacingModeHint(device?.label || '');
    }

    const settings = track.getSettings();
    const settingsFacingMode = this.detectFacingModeHint(typeof settings.facingMode === 'string' ? settings.facingMode : null);
    if (settingsFacingMode) {
      return settingsFacingMode;
    }

    const capabilities = typeof track.getCapabilities === 'function'
      ? (track.getCapabilities() as MediaTrackCapabilities & { facingMode?: string[] | string })
      : null;
    const capabilityFacingMode = Array.isArray(capabilities?.facingMode)
      ? (capabilities.facingMode.length === 1 ? capabilities.facingMode[0] : null)
      : capabilities?.facingMode;

    const capabilityHint = this.detectFacingModeHint(typeof capabilityFacingMode === 'string' ? capabilityFacingMode : null);
    if (capabilityHint) {
      return capabilityHint;
    }

    return this.detectFacingModeHint(device?.label || '');
  }

  private resolveTrackFacingMode(
    track?: MediaStreamTrack | null,
    device?: Pick<MediaDeviceInfo, 'label'> | null,
    fallback: 'user' | 'environment' = 'user'
  ): 'user' | 'environment' {
    return this.detectTrackFacingMode(track, device) || fallback;
  }

  private buildVideoConstraints(deviceId?: string | null): MediaTrackConstraints {
    if (deviceId) {
      return {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      };
    }

    return {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: this.preferredVideoFacingMode
    };
  }

  constructor() {
    // Load ICE servers from backend
    this.loadIceServers();
  }

  private observeLocalTrack(track: MediaStreamTrack, source: string): void {
    if (this.observedLocalTrackIds.has(track.id)) return;
    this.observedLocalTrackIds.add(track.id);

    const logState = (event: string) => {
      webrtcDebugLog('[WebRTC][localTrack]', {
        event,
        source,
        kind: track.kind,
        id: track.id,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState
      });
    };

    track.addEventListener('mute', () => {
      logState('mute');
    });
    track.addEventListener('unmute', () => {
      logState('unmute');
    });
    track.addEventListener('ended', () => {
      logState('ended');
    });

    logState('observed');
  }

  /**
   * Load ICE servers configuration from backend
   */
  private async loadIceServers(serverUrlHint?: string | null) {
    try {
      const serverUrl = (serverUrlHint || '').toString().trim()
        || (this.getCurrentCallAuthContext()?.serverUrl || '').toString().trim();

      // In multi-tenant setups the frontend host is not the API host.
      // If we don't know which server to target yet, keep defaults.
      if (!serverUrl) return;

      const response = await configAPI.getIceServers(serverUrl);
      if (response.status === 'ok' && response.result) {
        this.iceServers = response.result.iceServers;
        webrtcDebugLog('ICE servers loaded:', this.iceServers);
      }
    } catch (error) {
      // Use defaults on failure - already set in constructor
    }
  }

  /**
   * Refresh TURN credentials for a new call.
   * This keeps STUN/TURN URLs stable but rotates credentials per call.
   */
  private async refreshTurnCredentialsForCall() {
    try {
      const context = this.getCurrentCallAuthContext();
      const deviceId = (context?.deviceId || '').toString().trim();
      const serverUrl = (context?.serverUrl || '').toString().trim();
      const deviceSigningKey = context?.deviceSigningKey || null;
      if (!deviceId || !serverUrl || !deviceSigningKey) return;

      const signed = await signRequestWithKey(
        'config:turn-credentials',
        {},
        deviceId,
        deviceSigningKey
      );

      const response = await configAPI.getTurnCredentials(signed, serverUrl);
      if (response.status !== 'ok' || !response.result) return;

      const { urls, username, credential } = response.result;
      if (!urls || urls.length === 0) return;

      const idx = this.iceServers.findIndex((server) => this.isTurnServer(server));
      const updatedTurn: RTCIceServer = {
        urls,
        ...(username ? { username } : {}),
        ...(credential ? { credential } : {})
      };

      if (idx >= 0) {
        this.iceServers[idx] = updatedTurn;
      } else {
        this.iceServers.push(updatedTurn);
      }
    } catch {
      // Non-fatal: continue with existing ICE servers
    }
  }

  /**
   * Set callbacks for call events
   */
  setCallbacks(callbacks: WebRTCCallbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Get access to camera and microphone
   */
  async getLocalMedia(videoEnabled = false, audioEnabled = true): Promise<MediaStream> {
    this.releaseLocalMediaTracks();

    const constraints: MediaStreamConstraints = {
      video: videoEnabled ? this.buildVideoConstraints(this.selectedVideoInputDeviceId) : false,
      audio: audioEnabled ? {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      } : false
    };

    try {
      webrtcDebugLog('[WebRTC][getLocalMedia] request', {
        videoEnabled,
        audioEnabled,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        hasMediaDevices: !!navigator.mediaDevices,
        hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia
      });
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      const initialTrack = this.localStream.getVideoTracks()[0] || null;
      if (initialTrack) {
        const settings = initialTrack.getSettings();
        this.selectedVideoInputDeviceId = settings.deviceId || this.selectedVideoInputDeviceId;
        this.preferredVideoFacingMode = this.resolveTrackFacingMode(initialTrack, null, this.preferredVideoFacingMode);
        this.observeLocalTrack(initialTrack, 'getLocalMedia:video');
      }

      const initialAudioTrack = this.localStream.getAudioTracks()[0] || null;
      if (initialAudioTrack) {
        const settings = initialAudioTrack.getSettings();
        this.selectedAudioInputDeviceId = settings.deviceId || this.selectedAudioInputDeviceId;
        this.observeLocalTrack(initialAudioTrack, 'getLocalMedia:audio');
      }

      return this.localStream;
    } catch (error) {
      const isRetryableNotReadable =
        error instanceof DOMException
        && error.name === 'NotReadableError';
      if (isRetryableNotReadable) {
        webrtcDebugLog('[WebRTC][getLocalMedia] retry after NotReadableError');
        this.releaseLocalMediaTracks();
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        try {
          this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
          const initialTrack = this.localStream.getVideoTracks()[0] || null;
          if (initialTrack) {
            const settings = initialTrack.getSettings();
            this.selectedVideoInputDeviceId = settings.deviceId || this.selectedVideoInputDeviceId;
            this.preferredVideoFacingMode = this.resolveTrackFacingMode(initialTrack, null, this.preferredVideoFacingMode);
            this.observeLocalTrack(initialTrack, 'getLocalMedia:video:retry');
          }

          const initialAudioTrack = this.localStream.getAudioTracks()[0] || null;
          if (initialAudioTrack) {
            const settings = initialAudioTrack.getSettings();
            this.selectedAudioInputDeviceId = settings.deviceId || this.selectedAudioInputDeviceId;
            this.observeLocalTrack(initialAudioTrack, 'getLocalMedia:audio:retry');
          }

          return this.localStream;
        } catch (retryError) {
          console.error('Error getting local media after retry:', retryError);
          const retryMessage = retryError instanceof Error
            ? `${retryError.name || 'Error'}: ${retryError.message || 'Unknown error'}`
            : 'Failed to access microphone/camera';
          throw new Error(retryMessage);
        }
      }
      console.error('Error getting local media:', error);
      const message = error instanceof Error
        ? `${error.name || 'Error'}: ${error.message || 'Unknown error'}`
        : 'Failed to access microphone/camera';
      throw new Error(message);
    }
  }

  /**
   * Create RTCPeerConnection
   */
  private async createPeerConnection(callSessionId: CallSessionId) {
    this.currentCallSessionId = callSessionId;

    const callServerUrl = (this.getCurrentCallAuthContext()?.serverUrl || '').toString().trim();
    if (callServerUrl) {
      await this.loadIceServers(callServerUrl);
    }

    // Rotate TURN credentials per call.
    await this.refreshTurnCredentialsForCall();

    const iceServers = this.getIceServersForCall();
    this.peerConnection = new RTCPeerConnection({
      iceServers,
      iceTransportPolicy: this.getAlwaysTurnEnabled() ? 'relay' : 'all'
    });

    this.peerConnection.onsignalingstatechange = () => {
      this.dumpPeerConnectionState('signalingstatechange');
      void this.flushPendingRenegotiation('signalingstatechange');
    };

    this.peerConnection.onnegotiationneeded = () => {
      webrtcDebugLog('[WebRTC][pc] negotiationneeded');
      this.dumpPeerConnectionState('negotiationneeded');
    };

    // Always negotiate a video m-line so video can be enabled later without renegotiation.
    // Using a dedicated transceiver avoids relying on deprecated offerToReceiveVideo.
    try {
      const videoTransceiver = this.peerConnection.addTransceiver('video', { direction: 'sendrecv' });
      this.videoSender = videoTransceiver.sender;
      this.dumpPeerConnectionState('after addTransceiver(video)');
    } catch {
      this.videoSender = null;
      this.dumpPeerConnectionState('addTransceiver(video) failed');
    }

    // Add local tracks
    if (this.localStream) {
      // Audio tracks: regular addTrack
      this.localStream.getAudioTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, this.localStream!);
      });

      // Video track: attach to negotiated sender if present, otherwise fallback to addTrack
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        if (this.videoSender) {
          try {
            await this.videoSender.replaceTrack(videoTrack);
          } catch {
            this.peerConnection!.addTrack(videoTrack, this.localStream);
          }
        } else {
          this.peerConnection!.addTrack(videoTrack, this.localStream);
        }
      }
    }

    // Handle incoming tracks
    this.peerConnection.ontrack = (event) => {
      const track = event.track;
      if (track.kind === 'video') {
        webrtcDebugLog('[RemoteVideo][ontrack]', JSON.stringify({
          id: track.id,
          label: track.label,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          streams: (event.streams || []).map((s) => s.id)
        }));
      }
      webrtcDebugLog('[WebRTC][ontrack] received', {
        kind: track.kind,
        id: track.id,
        label: track.label,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        streams: (event.streams || []).map((s) => s.id)
      });

      if (!this.remoteStream) {
        this.remoteStream = new MediaStream();
        webrtcDebugLog('[WebRTC][ontrack] created remote MediaStream', { id: this.remoteStream.id });
      }

      if (track.kind === 'video') {
        for (const existingTrack of this.remoteStream.getVideoTracks()) {
          if (existingTrack.id === track.id) continue;
          try {
            this.remoteStream.removeTrack(existingTrack);
          } catch (e) {
            webrtcDebugLog('[WebRTC][ontrack] remoteStream.removeTrack(existing video) failed', e);
          }
        }
      }

      try {
        this.remoteStream.addTrack(track);
      } catch (e) {
        webrtcDebugLog('[WebRTC][ontrack] remoteStream.addTrack failed', e);
      }

      if (!this.observedRemoteTrackIds.has(track.id)) {
        this.observedRemoteTrackIds.add(track.id);
        track.addEventListener('mute', () => {
          webrtcDebugLog('[WebRTC][remoteTrack] mute', { kind: track.kind, id: track.id, readyState: track.readyState });
          if (track.kind === 'video') {
            const existingTimer = this.remoteVideoTrackMuteTimers.get(track.id);
            if (existingTimer !== undefined) {
              window.clearTimeout(existingTimer);
            }
            const timer = window.setTimeout(() => {
              this.remoteVideoTrackMuteTimers.delete(track.id);
              if (track.readyState === 'ended' || track.muted) {
                this.removeRemoteVideoTrack(track, track.readyState === 'ended' ? 'track-ended-after-mute' : 'track-muted-timeout');
              }
            }, 450);
            this.remoteVideoTrackMuteTimers.set(track.id, timer);
          }
        });
        track.addEventListener('unmute', () => {
          webrtcDebugLog('[WebRTC][remoteTrack] unmute', { kind: track.kind, id: track.id, readyState: track.readyState });
          if (track.kind === 'video') {
            const existingTimer = this.remoteVideoTrackMuteTimers.get(track.id);
            if (existingTimer !== undefined) {
              window.clearTimeout(existingTimer);
              this.remoteVideoTrackMuteTimers.delete(track.id);
            }
          }
        });
        track.addEventListener('ended', () => {
          webrtcDebugLog('[WebRTC][remoteTrack] ended', { kind: track.kind, id: track.id, readyState: track.readyState });
          if (track.kind === 'video') {
            this.removeRemoteVideoTrack(track, 'track-ended');
          }
        });
      }

      webrtcDebugLog('[WebRTC][ontrack] remote stream summary', {
        streamId: this.remoteStream.id,
        tracks: this.remoteStream.getTracks().map((t) => ({ kind: t.kind, id: t.id, muted: t.muted, readyState: t.readyState }))
      });
      if (track.kind === 'video') {
        webrtcDebugLog('[RemoteVideo][stream-summary]', JSON.stringify({
          streamId: this.remoteStream.id,
          tracks: this.remoteStream.getTracks().map((t) => ({
            kind: t.kind,
            id: t.id,
            enabled: t.enabled,
            muted: t.muted,
            readyState: t.readyState
          }))
        }));
      }

      this.dumpPeerConnectionState(`after ontrack(${track.kind})`);
      if (track.kind === 'video') {
        this.scheduleRemoteStreamReceiverResync('ontrack(video)');
      }

      if (this.callbacks.onRemoteStream) {
        this.callbacks.onRemoteStream(this.remoteStream);
      }
    };

    // ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateJson = event.candidate.toJSON();
        const parsed = this.parseCandidateForLog(candidateJson);
        webrtcDebugLog('[WebRTC][ice] local candidate gathered', {
          callSessionId,
          type: parsed.type,
          protocol: parsed.protocol,
          address: parsed.address,
          port: parsed.port,
          sdpMid: candidateJson.sdpMid,
          sdpMLineIndex: candidateJson.sdpMLineIndex
        });

        if (this.getAlwaysTurnEnabled() && !this.isRelayCandidate(candidateJson)) {
          webrtcDebugLog('[WebRTC][ice] dropping non-relay local candidate (relay-only mode)', {
            callSessionId,
            type: parsed.type,
            protocol: parsed.protocol,
            address: parsed.address,
            port: parsed.port
          });
          return;
        }
        webrtcDebugLog('[WebRTC][ice] sending local candidate to signaling server', {
          callSessionId,
          type: parsed.type,
          protocol: parsed.protocol
        });
        if (!this.signalingReadyForIce.has(callSessionId)) {
          webrtcDebugLog('[WebRTC][ice] buffering local candidate until initial signaling is sent', {
            callSessionId,
            type: parsed.type,
            protocol: parsed.protocol
          });
          this.enqueueOutgoingIceCandidate(callSessionId, candidateJson);
          return;
        }
        wsClient.sendIceCandidate(callSessionId, candidateJson);
      } else {
        webrtcDebugLog('[WebRTC][ice] local candidate gathering complete', { callSessionId });
      }
    };

    // Connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection!.connectionState;
      webrtcDebugLog('Connection state:', state);

      if (this.callbacks.onConnectionStateChange) {
        this.callbacks.onConnectionStateChange(state);
      }

      // Note: 'disconnected' can be transient (mobile backgrounding / NAT rebinding).
      // Let higher-level logic decide when to end the call.
      if (state === 'failed' || state === 'closed') {
        if (this.callbacks.onCallEnded) {
          this.callbacks.onCallEnded();
        }
      }

      // Chrome sometimes doesn't start the video RTP pipeline after the initial answer exchange.
      // A renegotiation triggered after the connection is fully established reliably fixes this.
      // At this point the caller has already processed the initial answer (stable state),
      // so there is no signaling race condition.
      if (state === 'connected' && this.pendingPostConnectVideoRenegotiation) {
        this.pendingPostConnectVideoRenegotiation = false;
        this.requestRenegotiation('video-init-after-connect');
      }
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      const iceState = this.peerConnection!.iceConnectionState;
      webrtcDebugLog('ICE connection state:', iceState);
      this.dumpPeerConnectionState(`iceconnectionstatechange:${iceState}`);
      if (this.callbacks.onIceConnectionStateChange) {
        this.callbacks.onIceConnectionStateChange(iceState);
      }
    };

    this.peerConnection.onicegatheringstatechange = () => {
      const gatheringState = this.peerConnection!.iceGatheringState;
      webrtcDebugLog('[WebRTC][ice] gathering state changed', {
        callSessionId,
        gatheringState
      });
      this.dumpPeerConnectionState(`icegatheringstatechange:${gatheringState}`);
    };

    this.peerConnection.onicecandidateerror = (event) => {
      webrtcDebugLog('[WebRTC][ice] candidate error', {
        callSessionId,
        address: event.address,
        port: event.port,
        url: event.url,
        errorCode: event.errorCode,
        errorText: event.errorText
      });
    };

    return this.peerConnection;
  }

  private enqueueIceCandidate(callSessionId: CallSessionId, candidate: RTCIceCandidateInit) {
    const list = this.pendingIceCandidates.get(callSessionId) || [];
    list.push(candidate);
    this.pendingIceCandidates.set(callSessionId, list);
  }

  private async flushIceCandidates(callSessionId: CallSessionId): Promise<void> {
    if (!this.peerConnection) return;
    if (this.currentCallSessionId !== callSessionId) return;
    if (!this.peerConnection.remoteDescription) return;

    const list = this.pendingIceCandidates.get(callSessionId);
    if (!list || list.length === 0) return;

    this.pendingIceCandidates.delete(callSessionId);
    for (const candidate of list) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        // Stale candidates after ICE restart are benign
      }
    }
  }

  /**
   * Initiate a call (create offer)
   */
  async initiateCall(
    targetIdentityId: string,
    callSessionId: CallSessionId,
    identityPrivateKey?: SigningKey,
    localContext?: CallAuthContext
  ): Promise<void> {
    if (!this.localStream) {
      throw new Error('No local stream. Call getLocalMedia() first.');
    }

    // Store target identity for potential ICE restart
    this.currentTargetIdentityId = targetIdentityId;
    this.setCurrentCallAuthContext({
      ...localContext,
      identitySigningKey: identityPrivateKey || localContext?.identitySigningKey,
      deviceSigningKey: localContext?.deviceSigningKey
    });

    await this.createPeerConnection(callSessionId);

    const offer = await this.peerConnection!.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });

    await this.peerConnection!.setLocalDescription(offer);

    // E2EE: Sign DTLS fingerprint (extracted from SDP) for MITM protection
    let dtlsSignature: string | undefined;

    if (identityPrivateKey && offer.sdp) {
      const fingerprint = extractDTLSFingerprint(offer.sdp);
      if (fingerprint) {
        dtlsSignature = await signDTLSFingerprintWithKey(fingerprint, identityPrivateKey);
      }
    }

    if (shouldCancelOutgoingCallSession(callSessionId)) {
      this.close();
      return;
    }

    // Send offer via WebSocket with signature
    wsClient.initiateCall(targetIdentityId, callSessionId, offer, dtlsSignature);
    this.markSignalingReadyForIce(callSessionId);
  }

  /**
   * Answer an incoming call
   */
  async answerCall(
    callSessionId: CallSessionId,
    offer: RTCSessionDescriptionInit,
    identityPrivateKey?: SigningKey,
    targetIdentityId?: string,
    localContext?: CallAuthContext
  ): Promise<void> {
    if (!this.localStream) {
      throw new Error('No local stream. Call getLocalMedia() first.');
    }

    // Store target identity for potential ICE restart
    if (targetIdentityId) {
      this.currentTargetIdentityId = targetIdentityId;
    }
    this.setCurrentCallAuthContext({
      ...localContext,
      identitySigningKey: identityPrivateKey || localContext?.identitySigningKey,
      deviceSigningKey: localContext?.deviceSigningKey
    });

    await this.createPeerConnection(callSessionId);

    await this.peerConnection!.setRemoteDescription(new RTCSessionDescription(offer));

    // On the answering side, the offer already contains a negotiated video m-line.
    // If we created a video transceiver before applying the offer, browsers can end up
    // with multiple transceivers; rebind to the negotiated one so replaceTrack affects
    // the active m-line.
    this.pickNegotiatedVideoSender('answerCall:after setRemoteDescription(offer)');
    await this.ensureLocalVideoTrackAttached('answerCall:after setRemoteDescription(offer)');

    // ICE candidates may arrive before the peer connection / remote description is ready.
    await this.flushIceCandidates(callSessionId);

    const answer = await this.peerConnection!.createAnswer();
    await this.peerConnection!.setLocalDescription(answer);

    this.pickNegotiatedVideoSender('answerCall:after setLocalDescription(answer)');
    await this.ensureLocalVideoTrackAttached('answerCall:after setLocalDescription(answer)');

    // E2EE: Sign DTLS fingerprint (extracted from SDP) for MITM protection
    let dtlsSignature: string | undefined;

    if (identityPrivateKey && answer.sdp) {
      const fingerprint = extractDTLSFingerprint(answer.sdp);
      if (fingerprint) {
        dtlsSignature = await signDTLSFingerprintWithKey(fingerprint, identityPrivateKey);
      }
    }

    // Send answer via WebSocket with signature
    wsClient.answerCall(callSessionId, answer, dtlsSignature);
    this.markSignalingReadyForIce(callSessionId);

    // If video is enabled, schedule a renegotiation after the connection is established.
    // This works around Chrome not starting the video RTP pipeline after the initial answer.
    if (this.localStream && this.localStream.getVideoTracks().length > 0) {
      this.pendingPostConnectVideoRenegotiation = true;
    }
  }

  /**
   * Verify remote DTLS fingerprint signature (E2EE security check)
   */
  async verifyRemoteDTLS(
    remoteSDP: string,
    dtlsSignature: string | undefined,
    _remoteIdentityId: string,
    remoteIdentityPublicKeyBase64: string
  ): Promise<boolean> {
    // SECURITY: the fingerprint we verify MUST come from SDP (what WebRTC will actually use).
    const fingerprintFromSdp = remoteSDP ? extractDTLSFingerprint(remoteSDP) : null;

    // If no signature or no fingerprint, cannot verify
    if (!fingerprintFromSdp || !dtlsSignature) {
      if (this.callbacks.onSecurityVerified) {
        this.callbacks.onSecurityVerified(false);
      }
      return false;
    }

    try {
      const publicKeyStr = atob(remoteIdentityPublicKeyBase64);
      const remotePublicKey = new Uint8Array(publicKeyStr.length);
      for (let i = 0; i < publicKeyStr.length; i++) {
        remotePublicKey[i] = publicKeyStr.charCodeAt(i);
      }

      // Verify signature
      const isValid = await verifyDTLSSignature(
        fingerprintFromSdp,
        dtlsSignature,
        remotePublicKey
      );

      if (isValid) {
        if (this.callbacks.onSecurityVerified) {
          this.callbacks.onSecurityVerified(true);
        }
      } else {
        console.error('❌ DTLS signature verification FAILED - possible MITM attack!');
        if (this.callbacks.onSecurityVerified) {
          this.callbacks.onSecurityVerified(false);
        }
      }

      return isValid;
    } catch (error) {
      console.error('Error verifying DTLS signature:', error);
      if (this.callbacks.onSecurityVerified) {
        this.callbacks.onSecurityVerified(false);
      }
      return false;
    }
  }

  /**
   * Handle answer from peer (for call initiator)
   */
  async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not created');
    }

    summarizeVideoSdp('handleAnswer:remoteAnswer', answer.sdp);
    await this.applyRemoteDescriptionAndVideoState(answer, 'handleAnswer');
  }

  /**
   * Add ICE candidate from peer
   */
  async addIceCandidate(callSessionId: CallSessionId, candidate: RTCIceCandidateInit): Promise<void> {
    // Candidates can arrive while we're still ringing / before answerCall created the RTCPeerConnection,
    // or before the remote description is set.
    if (this.getAlwaysTurnEnabled() && !this.isRelayCandidate(candidate)) {
      const parsed = this.parseCandidateForLog(candidate);
      webrtcDebugLog('[WebRTC][ice] dropping remote non-relay candidate (relay-only mode)', {
        callSessionId,
        type: parsed.type,
        protocol: parsed.protocol,
        address: parsed.address,
        port: parsed.port
      });
      return;
    }
    if (!this.peerConnection || this.currentCallSessionId !== callSessionId || !this.peerConnection.remoteDescription) {
      const parsed = this.parseCandidateForLog(candidate);
      webrtcDebugLog('[WebRTC][ice] buffering remote candidate until peerConnection/remoteDescription ready', {
        callSessionId,
        hasPeerConnection: !!this.peerConnection,
        callSessionMatches: this.currentCallSessionId === callSessionId,
        hasRemoteDescription: !!this.peerConnection?.remoteDescription,
        type: parsed.type,
        protocol: parsed.protocol
      });
      this.enqueueIceCandidate(callSessionId, candidate);
      return;
    }

    try {
      const parsed = this.parseCandidateForLog(candidate);
      webrtcDebugLog('[WebRTC][ice] applying remote candidate', {
        callSessionId,
        type: parsed.type,
        protocol: parsed.protocol,
        address: parsed.address,
        port: parsed.port
      });
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {
      // "Unknown ufrag" errors are benign: stale candidates from a previous ICE generation
      // arriving after an ICE restart has updated the remote description.
    }
  }

  /**
   * ICE restart (useful when network changes)
   */
  async restartIce(options?: { forceRelay?: boolean; refreshTurnCredentials?: boolean }): Promise<void> {
    if (!this.peerConnection || !this.currentCallSessionId || !this.currentTargetIdentityId) {
      throw new Error('No active call or target identity');
    }

    const alwaysTurn = this.getAlwaysTurnEnabled();
    const forceRelay = options?.forceRelay || alwaysTurn;

    if (options?.refreshTurnCredentials || forceRelay) {
      // Best effort: rotate TURN creds before forcing relay-only.
      await this.refreshTurnCredentialsForCall();
    }

    // Best effort: update PC configuration for relay-only ICE.
    // Note: some browsers may not fully apply iceTransportPolicy changes mid-call.
    try {
      const cfg = this.peerConnection.getConfiguration();
      cfg.iceServers = this.getIceServersForCall();
      if (forceRelay) {
        cfg.iceTransportPolicy = 'relay';
      } else if (cfg.iceTransportPolicy) {
        cfg.iceTransportPolicy = 'all';
      }
      this.peerConnection.setConfiguration(cfg);
    } catch {
      // Non-fatal: ICE restart will proceed with existing configuration
    }

    const offer = await this.peerConnection.createOffer({ iceRestart: true });
    summarizeVideoSdp('restartIce:localOffer', offer.sdp);
    await this.peerConnection.setLocalDescription(offer);

    // E2EE: sign DTLS fingerprint for renegotiation as well.
    let dtlsSignature: string | undefined;
    const identityPrivateKey = this.getCurrentIdentitySigningKey();
    if (identityPrivateKey && offer.sdp) {
      const fingerprint = extractDTLSFingerprint(offer.sdp);
      if (fingerprint) {
        dtlsSignature = await signDTLSFingerprintWithKey(fingerprint, identityPrivateKey);
      }
    }

    // Send renegotiation offer (do not create a new call session on the server).
    wsClient.renegotiateOffer(this.currentCallSessionId, offer, dtlsSignature);
  }

  /**
   * Handle renegotiation offer from peer (ICE restart / mid-call SDP update)
   */
  async handleRenegotiationOffer(callSessionId: CallSessionId, offer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection || this.currentCallSessionId !== callSessionId) {
      throw new Error('Peer connection not ready for renegotiation');
    }

    webrtcDebugLog('[RemoteVideo][renegotiate-offer]', JSON.stringify({
      callSessionId,
      hasSdp: !!offer?.sdp
    }));
    summarizeVideoSdp('handleRenegotiationOffer:remoteOffer', offer.sdp);
    await this.applyRemoteDescriptionAndVideoState(offer, 'handleRenegotiationOffer');

    const answer = await this.peerConnection.createAnswer();
    webrtcDebugLog('[RemoteVideo][renegotiate-offer:createAnswer]', JSON.stringify({
      callSessionId,
      localTracks: this.localStream?.getTracks().map((t) => ({
        kind: t.kind,
        id: t.id,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState
      })) || []
    }));
    summarizeVideoSdp('handleRenegotiationOffer:localAnswer', answer.sdp);
    await this.peerConnection.setLocalDescription(answer);

    this.pickNegotiatedVideoSender('handleRenegotiationOffer:after setLocalDescription(answer)');
    await this.ensureLocalVideoTrackAttached('handleRenegotiationOffer:after setLocalDescription(answer)');

    let dtlsSignature: string | undefined;
    const identityPrivateKey = this.getCurrentIdentitySigningKey();
    if (identityPrivateKey && answer.sdp) {
      const fingerprint = extractDTLSFingerprint(answer.sdp);
      if (fingerprint) {
        dtlsSignature = await signDTLSFingerprintWithKey(fingerprint, identityPrivateKey);
      }
    }

    wsClient.renegotiateAnswer(callSessionId, answer, dtlsSignature);
  }

  /**
   * Handle renegotiation answer from peer (initiator side)
   */
  async handleRenegotiationAnswer(callSessionId: CallSessionId, answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection || this.currentCallSessionId !== callSessionId) {
      throw new Error('Peer connection not ready for renegotiation');
    }
    webrtcDebugLog('[RemoteVideo][renegotiate-answer]', JSON.stringify({
      callSessionId,
      hasSdp: !!answer?.sdp,
      remoteStreamTracksBefore: this.remoteStream?.getTracks().map((t) => ({
        kind: t.kind,
        id: t.id,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState
      })) || []
    }));
    summarizeVideoSdp('handleRenegotiationAnswer:remoteAnswer', answer.sdp);
    await this.applyRemoteDescriptionAndVideoState(answer, 'handleRenegotiationAnswer');
    webrtcDebugLog('[RemoteVideo][renegotiate-answer:after-setRemoteDescription]', JSON.stringify({
      callSessionId,
      remoteStreamTracksAfter: this.remoteStream?.getTracks().map((t) => ({
        kind: t.kind,
        id: t.id,
        enabled: t.enabled,
        muted: t.muted,
        readyState: t.readyState
      })) || []
    }));
  }

  private pickSelectedCandidatePair(stats: RTCStatsReport): RTCStats | null {
    // Prefer the spec-driven way: transport.selectedCandidatePairId (Chrome/Edge/Firefox variants).
    let selectedPairId: string | undefined;
    for (const r of stats.values()) {
      if (r.type === 'transport') {
        const anyR = r as unknown as { selectedCandidatePairId?: string };
        if (anyR.selectedCandidatePairId) {
          selectedPairId = anyR.selectedCandidatePairId;
          break;
        }
      }
    }

    if (selectedPairId) {
      const pair = stats.get(selectedPairId);
      if (pair && pair.type === 'candidate-pair') return pair;
    }

    // Fallback: heuristics.
    let best: RTCStats | null = null;
    for (const r of stats.values()) {
      if (r.type !== 'candidate-pair') continue;
      const anyR = r as unknown as { state?: string; nominated?: boolean; selected?: boolean; currentRoundTripTime?: number };
      const isSelected = anyR.selected === true;
      const isGood = anyR.state === 'succeeded' && (anyR.nominated === true || isSelected);
      if (!isGood) continue;
      if (!best) {
        best = r;
        continue;
      }
      const bestRtt = (best as unknown as { currentRoundTripTime?: number }).currentRoundTripTime ?? Number.POSITIVE_INFINITY;
      const rtt = anyR.currentRoundTripTime ?? Number.POSITIVE_INFINITY;
      if (rtt < bestRtt) best = r;
    }

    return best;
  }

  /**
   * Debug helper: returns the currently selected ICE candidate pair.
   * Uses getStats() and best-effort mapping across browsers.
   */
  async getIceDebugInfo(): Promise<IceDebugInfo | null> {
    if (!this.peerConnection) return null;

    const pc = this.peerConnection;
    const base: IceDebugInfo = {
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceGatheringState: pc.iceGatheringState
    };

    try {
      const stats = await pc.getStats();
      const pair = this.pickSelectedCandidatePair(stats);
      if (!pair) return base;

      const anyPair = pair as unknown as {
        localCandidateId?: string;
        remoteCandidateId?: string;
        state?: string;
        nominated?: boolean;
        currentRoundTripTime?: number;
        availableOutgoingBitrate?: number;
        bytesSent?: number;
        bytesReceived?: number;
      };

      const local = anyPair.localCandidateId ? (stats.get(anyPair.localCandidateId) as RTCStats | undefined) : undefined;
      const remote = anyPair.remoteCandidateId ? (stats.get(anyPair.remoteCandidateId) as RTCStats | undefined) : undefined;

      const mapCandidate = (c?: RTCStats): IceCandidateDebugInfo | undefined => {
        if (!c) return undefined;
        const anyC = c as unknown as {
          candidateType?: string;
          protocol?: string;
          address?: string;
          ip?: string;
          port?: number;
          url?: string;
          relayProtocol?: string;
          networkType?: string;
        };
        return {
          candidateType: anyC.candidateType,
          protocol: anyC.protocol,
          address: anyC.address,
          ip: anyC.ip,
          port: anyC.port,
          url: anyC.url,
          relayProtocol: anyC.relayProtocol,
          networkType: anyC.networkType
        };
      };

      return {
        ...base,
        local: mapCandidate(local),
        remote: mapCandidate(remote),
        pair: {
          state: anyPair.state,
          nominated: anyPair.nominated,
          currentRoundTripTime: anyPair.currentRoundTripTime,
          availableOutgoingBitrate: anyPair.availableOutgoingBitrate,
          bytesSent: anyPair.bytesSent,
          bytesReceived: anyPair.bytesReceived
        }
      };
    } catch {
      return base;
    }
  }

  /**
   * Toggle video on/off
   */
  async toggleVideo(): Promise<boolean> {
    if (!this.peerConnection || !this.localStream) {
      webrtcDebugLog('[WebRTC][toggleVideo] aborted: peerConnection/localStream missing', {
        hasPeerConnection: !!this.peerConnection,
        hasLocalStream: !!this.localStream
      });
      return false;
    }

    this.pickNegotiatedVideoSender('toggleVideo:pre');

    webrtcDebugLog('[WebRTC][toggleVideo] invoked', {
      currentlySending: !!this.videoSender?.track,
      hasVideoSender: !!this.videoSender,
      localVideoTracks: this.localStream.getVideoTracks().map((t) => ({ id: t.id, readyState: t.readyState, muted: t.muted, enabled: t.enabled }))
    });

    // Prefer controlling the negotiated sender to avoid needing renegotiation.
    if (!this.videoSender) {
      // Best-effort: create a sender if it doesn't exist (older path). This may still require renegotiation
      // on some browsers, but it improves behavior in common cases.
      try {
        const videoTransceiver = this.peerConnection.addTransceiver('video', { direction: 'sendrecv' });
        this.videoSender = videoTransceiver.sender;
        webrtcDebugLog('[WebRTC][toggleVideo] created video transceiver/sender');
        this.dumpPeerConnectionState('toggleVideo: after addTransceiver(video)');
      } catch {
        // ignore
        webrtcDebugLog('[WebRTC][toggleVideo] failed to addTransceiver(video)');
      }
    }

    const currentlySending = !!this.videoSender?.track;
    if (currentlySending) {
      const currentTrack = this.videoSender!.track!;
      webrtcDebugLog('[WebRTC][toggleVideo] turning camera OFF', { trackId: currentTrack.id, readyState: currentTrack.readyState });

      // Make sure the m-line is allowed to stop sending.
      this.trySetVideoDirection('recvonly', 'toggleVideo:off');
      try {
        await this.videoSender!.replaceTrack(null);
      } catch {
        // Non-fatal: video sender may already be detached
      }
      try {
        this.localStream.removeTrack(currentTrack);
      } catch {
        // ignore
      }
      try {
        currentTrack.stop();
      } catch {
        // ignore
      }

      // Some browsers won't fully propagate "track removed" to the remote without SDP update.
      // Renegotiation is safe here (stable signaling) and helps keep receiver state consistent.
      this.requestRenegotiation('video-off');
      return false;
    }

    // If we already have a local video track (e.g. camera enabled previously) but sender got rebound
    // and lost the track, just re-attach it instead of re-acquiring camera.
    const existingLocalTrack = this.localStream.getVideoTracks()[0];
    if (existingLocalTrack && this.videoSender && !this.videoSender.track) {
      webrtcDebugLog('[WebRTC][toggleVideo] sender missing track, re-attaching existing local track', {
        trackId: existingLocalTrack.id,
        readyState: existingLocalTrack.readyState,
        muted: existingLocalTrack.muted
      });
      await this.videoSender.replaceTrack(existingLocalTrack);
      this.trySetVideoDirection('sendrecv', 'toggleVideo:on:reattach');
      this.dumpPeerConnectionState('toggleVideo: after reattach existing track');
      this.requestRenegotiation('video-on');
      return true;
    }

    // Turn camera on
    let videoStream: MediaStream;
    try {
      videoStream = await navigator.mediaDevices.getUserMedia({
        video: this.buildVideoConstraints(this.selectedVideoInputDeviceId),
        audio: false
      });
    } catch (e) {
      webrtcDebugLog('[WebRTC][toggleVideo] getUserMedia(video) failed', e);
      return false;
    }
    const newVideoTrack = videoStream.getVideoTracks()[0];
    if (!newVideoTrack) return false;

    webrtcDebugLog('[WebRTC][toggleVideo] turning camera ON (new track)', {
      id: newVideoTrack.id,
      readyState: newVideoTrack.readyState,
      muted: newVideoTrack.muted,
      enabled: newVideoTrack.enabled
    });
    const settings = newVideoTrack.getSettings();
    this.selectedVideoInputDeviceId = settings.deviceId || this.selectedVideoInputDeviceId;
    this.preferredVideoFacingMode = this.resolveTrackFacingMode(newVideoTrack, null, this.preferredVideoFacingMode);

    // Ensure the negotiated m-line permits sending.
    this.trySetVideoDirection('sendrecv', 'toggleVideo:on');

    this.localStream.addTrack(newVideoTrack);

    if (this.videoSender) {
      await this.videoSender.replaceTrack(newVideoTrack);
      webrtcDebugLog('[WebRTC][toggleVideo] attached track via replaceTrack', { senderTrackId: this.videoSender.track?.id || null });
    } else {
      this.peerConnection.addTrack(newVideoTrack, this.localStream);
      webrtcDebugLog('[WebRTC][toggleVideo] attached track via addTrack (may require renegotiation)', { trackId: newVideoTrack.id });
    }

    this.dumpPeerConnectionState('toggleVideo: after attach');

    // Even with a pre-negotiated m-line, some implementations need an SDP refresh
    // when going from "no sender track" -> "sender track" mid-call.
    this.requestRenegotiation('video-on');

    return true;
  }

  isScreenShareSupported(): boolean {
    return typeof navigator !== 'undefined'
      && !!navigator.mediaDevices
      && typeof navigator.mediaDevices.getDisplayMedia === 'function';
  }

  isScreenSharing(): boolean {
    const track = this.screenShareStream?.getVideoTracks?.()[0] || null;
    return !!track && track.readyState === 'live';
  }

  async startScreenShare(options?: { includeDisplayAudio?: boolean; includeMicrophone?: boolean }): Promise<boolean> {
    if (!this.peerConnection || !this.localStream || !this.videoSender || !this.isScreenShareSupported()) {
      return false;
    }
    if (this.isScreenSharing()) return true;

    let displayStream: MediaStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: !!options?.includeDisplayAudio
      });
    } catch (error) {
      webrtcDebugLog('[WebRTC][screenShare] getDisplayMedia failed', error);
      return false;
    }

    const displayTrack = displayStream.getVideoTracks()[0];
    if (!displayTrack) {
      displayStream.getTracks().forEach((track) => track.stop());
      return false;
    }

    this.screenShareStream = displayStream;
    this.previousCameraTrack = this.localStream.getVideoTracks()[0] || null;
    this.previousAudioTrack = this.getAudioSender()?.track || this.localStream.getAudioTracks()[0] || null;

    if (this.previousCameraTrack) {
      try {
        this.localStream.removeTrack(this.previousCameraTrack);
      } catch {
        // ignore
      }
    }

    this.localStream.addTrack(displayTrack);
    this.trySetVideoDirection('sendrecv', 'screenShare:on');
    await this.videoSender.replaceTrack(displayTrack);

    const audioSender = this.getAudioSender();
    const displayAudioTrack = displayStream.getAudioTracks()[0] || null;
    if (audioSender && displayAudioTrack && options?.includeDisplayAudio) {
      const shouldMixWithMicrophone = !!options.includeMicrophone && !!this.previousAudioTrack;
      if (shouldMixWithMicrophone) {
        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();

        const displaySource = audioContext.createMediaStreamSource(new MediaStream([displayAudioTrack]));
        displaySource.connect(destination);

        const microphoneSource = audioContext.createMediaStreamSource(new MediaStream([this.previousAudioTrack!]));
        microphoneSource.connect(destination);

        const mixedTrack = destination.stream.getAudioTracks()[0] || null;
        if (mixedTrack) {
          this.screenShareAudioContext = audioContext;
          this.screenShareMixedAudioTrack = mixedTrack;
          await audioSender.replaceTrack(mixedTrack);
        } else {
          await audioSender.replaceTrack(displayAudioTrack);
        }
      } else {
        await audioSender.replaceTrack(displayAudioTrack);
      }
    }

    this.requestRenegotiation('screen-share-on');

    displayTrack.addEventListener('ended', () => {
      void this.stopScreenShare();
    }, { once: true });

    return true;
  }

  async stopScreenShare(): Promise<boolean> {
    if (!this.localStream || !this.videoSender) return false;
    if (!this.isScreenSharing()) return false;

    const displayTrack = this.screenShareStream?.getVideoTracks?.()[0] || null;
    if (displayTrack) {
      try {
        this.localStream.removeTrack(displayTrack);
      } catch {
        // ignore
      }
    }

    if (this.previousCameraTrack && this.previousCameraTrack.readyState === 'live') {
      this.localStream.addTrack(this.previousCameraTrack);
      this.trySetVideoDirection('sendrecv', 'screenShare:off:restore-camera');
      await this.videoSender.replaceTrack(this.previousCameraTrack);
    } else {
      this.trySetVideoDirection('recvonly', 'screenShare:off:no-camera');
      await this.videoSender.replaceTrack(null);
    }

    const audioSender = this.getAudioSender();
    if (audioSender) {
      if (this.previousAudioTrack && this.previousAudioTrack.readyState === 'live') {
        await audioSender.replaceTrack(this.previousAudioTrack);
      }
      if (this.screenShareMixedAudioTrack) {
        try {
          this.screenShareMixedAudioTrack.stop();
        } catch {
          // ignore
        }
      }
      if (this.screenShareAudioContext) {
        try {
          void this.screenShareAudioContext.close();
        } catch {
          // ignore
        }
      }
    }

    if (displayTrack) {
      try {
        displayTrack.stop();
      } catch {
        // ignore
      }
    }
    if (this.screenShareStream) {
      try {
        this.screenShareStream.getTracks().forEach((track) => track.stop());
      } catch {
        // ignore
      }
    }

    this.screenShareStream = null;
    this.previousCameraTrack = null;
    this.previousAudioTrack = null;
    this.screenShareAudioContext = null;
    this.screenShareMixedAudioTrack = null;
    this.requestRenegotiation('screen-share-off');
    return this.isVideoEnabled();
  }

  async switchCamera(): Promise<boolean> {
    if (!this.localStream || !this.videoSender || this.isScreenSharing()) return false;

    const currentTrack = this.localStream.getVideoTracks()[0] || null;
    if (!currentTrack) return false;

    const videoInputs = await this.getVideoInputDevices();
    if (videoInputs.length < 2) return false;

    const currentSettings = currentTrack.getSettings();
    const currentDeviceId = this.selectedVideoInputDeviceId || currentSettings.deviceId || '';
    const currentDevice = currentDeviceId
      ? videoInputs.find((device) => device.deviceId === currentDeviceId) || null
      : null;
    let currentIndex = currentDeviceId
      ? videoInputs.findIndex((device) => device.deviceId === currentDeviceId)
      : -1;

    const currentFacing = this.resolveTrackFacingMode(currentTrack, currentDevice, this.preferredVideoFacingMode);

    if (currentIndex < 0) {
      currentIndex = videoInputs.findIndex((device) => this.inferFacingMode(device) === currentFacing);
    }

    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % videoInputs.length
      : 0;
    const nextDevice = videoInputs[nextIndex];
    if (!nextDevice) return false;

    let nextStream: MediaStream;
    try {
      nextStream = await navigator.mediaDevices.getUserMedia({
        video: this.buildVideoConstraints(nextDevice.deviceId),
        audio: false
      });
    } catch {
      return false;
    }

    const nextTrack = nextStream.getVideoTracks()[0] || null;
    if (!nextTrack) {
      nextStream.getTracks().forEach((track) => track.stop());
      return false;
    }

    nextTrack.enabled = currentTrack.enabled;

    try {
      this.trySetVideoDirection('sendrecv', 'switchCamera');
      await this.videoSender.replaceTrack(nextTrack);
    } catch {
      nextTrack.stop();
      return false;
    }

    try {
      this.localStream.removeTrack(currentTrack);
    } catch {
      // ignore
    }
    try {
      currentTrack.stop();
    } catch {
      // ignore
    }

    this.localStream.addTrack(nextTrack);
    nextStream.getTracks().forEach((track) => {
      if (track !== nextTrack) {
        try {
          track.stop();
        } catch {
          // ignore
        }
      }
    });

    const nextSettings = nextTrack.getSettings();
    this.selectedVideoInputDeviceId = nextSettings.deviceId || nextDevice.deviceId || null;
    const nextFacingHint = this.detectTrackFacingMode(nextTrack, nextDevice);
    this.preferredVideoFacingMode = nextFacingHint
      || (this.selectedVideoInputDeviceId && this.selectedVideoInputDeviceId !== currentDeviceId
        ? (currentFacing === 'user' ? 'environment' : 'user')
        : currentFacing);
    this.requestRenegotiation('camera-switch');
    return true;
  }

  /**
   * Toggle audio on/off
   */
  toggleAudio(): boolean {
    if (!this.localStream) return false;

    const audioTrack = this.localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      return audioTrack.enabled;
    }
    return false;
  }

  async switchAudioInput(deviceId: string): Promise<boolean> {
    if (!this.localStream) return false;

    let nextStream: MediaStream;
    try {
      nextStream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        video: false
      });
    } catch {
      return false;
    }

    const nextTrack = nextStream.getAudioTracks()[0];
    if (!nextTrack) {
      nextStream.getTracks().forEach((track) => track.stop());
      return false;
    }

    const currentTrack = this.localStream.getAudioTracks()[0] || null;
    const currentEnabled = currentTrack ? currentTrack.enabled : true;
    nextTrack.enabled = currentEnabled;

    const sender = this.getAudioSender();
    try {
      if (sender) {
        await sender.replaceTrack(nextTrack);
      } else if (this.peerConnection) {
        this.peerConnection.addTrack(nextTrack, this.localStream);
      }
    } catch {
      nextTrack.stop();
      return false;
    }

    if (currentTrack) {
      try {
        this.localStream.removeTrack(currentTrack);
      } catch {
        // ignore
      }
      try {
        currentTrack.stop();
      } catch {
        // ignore
      }
    }

    const nextSettings = nextTrack.getSettings();
    this.selectedAudioInputDeviceId = nextSettings.deviceId || deviceId || null;
    this.observeLocalTrack(nextTrack, 'switchAudioInput');
    this.localStream.addTrack(nextTrack);
    nextStream.getTracks().forEach((track) => {
      if (track !== nextTrack) {
        try {
          track.stop();
        } catch {
          // ignore
        }
      }
    });

    return true;
  }

  /**
   * Check if video is enabled
   */
  isVideoEnabled(): boolean {
    const senderTrack = this.videoSender?.track;
    return !!senderTrack && senderTrack.enabled && senderTrack.readyState === 'live';
  }

  getCurrentVideoFacingMode(): 'user' | 'environment' {
    return this.preferredVideoFacingMode;
  }

  hasActiveLocalVideoTrack(): boolean {
    if (!this.localStream) return false;
    return this.localStream.getVideoTracks().some((track) => track.readyState === 'live');
  }

  /**
   * Check if audio is enabled
   */
  isAudioEnabled(): boolean {
    if (!this.localStream) return false;
    const audioTrack = this.localStream.getAudioTracks()[0];
    return audioTrack ? audioTrack.enabled : false;
  }

  getLocalAudioTrackState(): {
    id: string | null;
    enabled: boolean;
    muted: boolean;
    readyState: MediaStreamTrackState | 'missing';
  } {
    const audioTrack = this.localStream?.getAudioTracks?.()[0] || null;
    if (!audioTrack) {
      return {
        id: null,
        enabled: false,
        muted: false,
        readyState: 'missing'
      };
    }

    return {
      id: audioTrack.id,
      enabled: audioTrack.enabled,
      muted: audioTrack.muted,
      readyState: audioTrack.readyState
    };
  }

  hasHealthyLocalAudioTrack(): boolean {
    const state = this.getLocalAudioTrackState();
    return state.readyState === 'live' && state.enabled;
  }

  async ensureLocalAudioTrackHealthy(reason = 'unknown'): Promise<boolean> {
    const state = this.getLocalAudioTrackState();
    const trackLooksHealthy = state.readyState === 'live' && state.enabled && !state.muted;
    if (trackLooksHealthy) return true;

    webrtcDebugLog('[WebRTC][localAudio] attempting recovery', {
      reason,
      state,
      selectedAudioInputDeviceId: this.selectedAudioInputDeviceId
    });

    try {
      const replaced = await this.switchAudioInput(this.selectedAudioInputDeviceId || '');
      const nextState = this.getLocalAudioTrackState();
      webrtcDebugLog('[WebRTC][localAudio] recovery result', {
        reason,
        replaced,
        state: nextState
      });
      return replaced && nextState.readyState === 'live' && nextState.enabled;
    } catch {
      return false;
    }
  }

  /**
   * Get local stream
   */
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Get remote stream
   */
  getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  /**
   * End call and cleanup
   */
  endCall() {
    if (this.currentCallSessionId) {
      wsClient.endCall(this.currentCallSessionId);
    }

    this.close();
  }

  /**
   * Close connection and cleanup
   */
  close() {
    if (this.screenShareStream) {
      this.screenShareStream.getTracks().forEach(track => track.stop());
      this.screenShareStream = null;
    }
    if (this.screenShareMixedAudioTrack) {
      try {
        this.screenShareMixedAudioTrack.stop();
      } catch {
        // ignore
      }
      this.screenShareMixedAudioTrack = null;
    }
    if (this.screenShareAudioContext) {
      try {
        void this.screenShareAudioContext.close();
      } catch {
        // ignore
      }
      this.screenShareAudioContext = null;
    }
    this.previousCameraTrack = null;
    this.previousAudioTrack = null;

    if (this.localStream) {
      this.releaseLocalMediaTracks();
    }

    this.videoSender = null;

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    if (this.pendingRemoteReceiverResyncTimer !== null) {
      window.clearTimeout(this.pendingRemoteReceiverResyncTimer);
      this.pendingRemoteReceiverResyncTimer = null;
    }
    this.pendingRemoteReceiverResyncAttempts = 0;
    this.remoteVideoReceiverSnapshots.clear();
    this.pendingPostConnectVideoRenegotiation = false;
    this.signalingReadyForIce.clear();
    this.currentCallSessionId = null;
    this.currentTargetIdentityId = null;
    this.currentCallAuthContext = null;
    this.pendingIceCandidates.clear();
    this.pendingOutgoingIceCandidates.clear();
    this.observedLocalTrackIds.clear();
  }
}

// Export singleton instance
export const webrtcManager = new WebRTCManager();
